// Pure bridge between the query-language AST (queryLang.js) and the nested
// filter-builder's editable tree, so the WYSIWYG panel and the text bar are two
// views of the same query.
//
// Builder tree (id-free; the React panel assigns ids):
//   Group = { kind: "group", combinator: "and"|"or", children: Node[] }
//   Cond  = { kind: "cond", field, op, value: string, negated: bool }
//   Raw   = { kind: "raw", text }   // a clause the panel can't model as a row
//                                      (bare full-text term, NOT-of-a-group,
//                                      parse error) — preserved verbatim.
//
// astToBuilder normalizes to a single root Group; builderToAst is its inverse,
// re-parsing Raw text so everything funnels back through serializeQuery. The
// round-trip is semantic, not byte-identical (e.g. `a,b` IN ⇄ an OR group).

import { QUERY_FIELDS, OPS_BY_KIND, parseQuery, serializeQuery } from "./queryLang.js";

function group(combinator, children) {
  return { kind: "group", combinator, children };
}

// AST node → builder Node. Preds become Conds; not(pred) folds into a negated
// Cond; everything else the panel can't represent (text, not-of-group, error)
// is kept as Raw so it survives a round-trip untouched.
function nodeToBuilder(node) {
  if (!node) return null;
  switch (node.type) {
    case "and":
    case "or":
      return group(node.type, node.children.map(nodeToBuilder).filter(Boolean));
    case "pred":
      return predToCond(node, false);
    case "not": {
      const inner = node.child;
      if (inner && inner.type === "pred") return predToCond(inner, true);
      return { kind: "raw", text: serializeQuery(node) };
    }
    case "text":
    case "error":
      return { kind: "raw", text: serializeQuery(node) };
    default:
      return null;
  }
}

function predToCond(pred, negated) {
  // Normalize `!=` (ne) into negated-eq so the builder has ONE "is not" (rather
  // than both `field!=v` and `-field:v`); a NOT wrapper around a `!=` double-
  // negates back to eq. `in` (comma-list) shows as a comma-separated value;
  // contains keeps its `*`; other ops are scalar.
  let op = pred.op;
  let neg = negated;
  if (op === "ne") { op = "eq"; neg = !neg; }
  const value = op === "in"
    ? (Array.isArray(pred.value) ? pred.value.join(", ") : String(pred.value ?? ""))
    : String(pred.value ?? "");
  return { kind: "cond", field: pred.key, op, value, negated: neg };
}

// Parsed AST → the panel's root Group. A non-group root (single pred, text, …)
// is wrapped in an implicit AND group so the panel always has one root to edit.
export function astToBuilder(ast) {
  const built = nodeToBuilder(ast);
  if (!built) return group("and", []);
  if (built.kind === "group") return built;
  return group("and", [built]);
}

// Builder Node → AST node (or null when empty / incomplete, so the caller drops
// it). Cond → pred (wrapped in `not` when negated); Group collapses a single
// child and drops empties; Raw is re-parsed back to its AST.
export function builderToAst(node) {
  if (!node) return null;
  if (node.kind === "raw") {
    return node.text && node.text.trim() ? (parseQuery(node.text).ast || null) : null;
  }
  if (node.kind === "cond") {
    // Incomplete rows (no field, or no value for a value-bearing op) are
    // skipped rather than emitting a broken clause.
    if (!node.field || !QUERY_FIELDS[node.field]) return null;
    const v = String(node.value ?? "").trim();
    if (v === "") return null;
    const def = QUERY_FIELDS[node.field];
    let value;
    if (node.op === "in") {
      value = v.split(",").map((s) => s.trim()).filter(Boolean);
    } else if (node.op === "contains") {
      // contains serializes as bare `field:value`, and the parser only re-reads
      // it as contains when the value carries a `*`. A wildcard-free value would
      // otherwise round-trip to eq (a silently different result set), so wrap it.
      value = v.includes("*") ? v : `*${v}*`;
    } else {
      value = v;
    }
    const pred = {
      type: "pred", key: node.field, def, field: def.field ?? null,
      op: node.op, value, wildcard: node.op === "contains",
    };
    return node.negated ? { type: "not", child: pred } : pred;
  }
  // group
  const children = node.children.map(builderToAst).filter(Boolean);
  if (children.length === 0) return null;
  if (children.length === 1) return children[0];
  return { type: node.combinator, children };
}

// Builder root Group → query text (the inverse of parseQuery for the panel).
export function builderToQuery(root) {
  return serializeQuery(builderToAst(root));
}

// ── Operator catalog (shared with FilterBuilder; pure + tested here) ──
//
// Friendly operator choices mapping to the language's (op, negated). "is not"
// is negated-eq (matching predToCond's ne→negated-eq normalization), so the
// panel exposes a single "is not" rather than both `!=` and `-field:`.
export const UI_OPS = {
  is: { op: "eq", negated: false, label: "is" },
  is_not: { op: "eq", negated: true, label: "is not" },
  any_of: { op: "in", negated: false, label: "is any of" },
  contains: { op: "contains", negated: false, label: "contains" },
  not_contains: { op: "contains", negated: true, label: "doesn't contain" },
  gt: { op: "gt", negated: false, label: "greater than" },
  gte: { op: "gte", negated: false, label: "at least" },
  lt: { op: "lt", negated: false, label: "less than" },
  lte: { op: "lte", negated: false, label: "at most" },
};

// Reverse-map a cond's (op, negated) to its UI operator id, defaulting to "is".
export function uiOpId(cond) {
  for (const [id, m] of Object.entries(UI_OPS)) {
    if (m.op === cond.op && m.negated === Boolean(cond.negated)) return id;
  }
  return "is";
}

// Valid UI operators for a field: is/is-not are universal (the parser accepts
// `field:value` for any kind), plus the kind-specific ops from OPS_BY_KIND.
// Returns [{ value: uiOpId, label }].
export function opsForField(fieldKey) {
  const kind = QUERY_FIELDS[fieldKey]?.kind;
  const lang = OPS_BY_KIND[kind] || new Set(["eq"]);
  const ids = ["is", "is_not"];
  if (lang.has("in")) ids.push("any_of");
  if (lang.has("contains")) ids.push("contains", "not_contains");
  if (lang.has("gt")) ids.push("gt", "gte", "lt", "lte");
  return ids.map((id) => ({ value: id, label: UI_OPS[id].label }));
}
